import React, { useCallback, useEffect } from 'react';
import {
  Button,
  Popover,
  PopoverOrigin,
  styled,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import {
  DateRangePickerDay as MuiDateRangePickerDay,
  DateRangePickerDayProps
} from '@mui/x-date-pickers-pro/DateRangePickerDay';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import Box from '@mui/material/Box';
import { DateTime } from 'luxon';
import { MIN_SUPPORTED_DATE } from 'utils/date-time';

const useStyles = makeStyles((theme) => ({
  button: {
    whiteSpace: 'nowrap',
    textTransform: 'none',
    padding: theme.spacing(0),
    color: theme.palette.text.primary,
    '&:hover': {
      background: 'transparent'
    }
  },
  period: {
    display: 'flex',
    alignItems: 'center',
    padding: `${0} ${theme.spacing(5)} 0 ${theme.spacing(0.5)}`,
    color: theme.palette.text.primary
  }
}));
const usePopoverStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(0, 0, 1.5, 0)
  }
}));

const usePickersDayStyles = makeStyles(() => ({
  root: {
    '&:last-of-type': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0
    },
    '&:first-of-type': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0
    }
  },
  day: {
    borderRadius: 0
  }
}));

const anchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'center'
};
const transformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'center'
};

const DateRangePickerDay = styled(MuiDateRangePickerDay)(
  ({ theme, isHighlighting, outsideCurrentMonth }) => ({
    borderRadius: 0,
    ...(isHighlighting &&
      !outsideCurrentMonth && {
        backgroundColor: theme.palette.insights.dateRangePickerDayBgColor
      })
  })
) as React.ComponentType<DateRangePickerDayProps<DateTime>>;

type InsightsDateRangePickerProps = {
  label: string;
  handleChangeDateRange: (value: DateRange<DateTime | null>) => void;
  value: DateRange<DateTime | null>;
};

const InsightsDateRangePicker: React.FC<InsightsDateRangePickerProps> = ({
  label,
  handleChangeDateRange,
  value
}: InsightsDateRangePickerProps) => {
  const classes = useStyles();
  const popoverClasses = usePopoverStyles();
  const PickersDayClasses = usePickersDayStyles();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const [localValue, setLocalValue] = React.useState<
    DateRange<DateTime | null>
  >(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleAccept = useCallback(
    (_value: DateRange<DateTime>) => {
      setLocalValue(_value);
      handleChangeDateRange(_value);
      setAnchorEl(null);
    },
    [handleChangeDateRange]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    if (localValue.toString() !== value.toString()) {
      handleChangeDateRange(localValue);
    }
  }, [handleChangeDateRange, localValue, value]);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleDateChange = useCallback((_value: DateRange<DateTime>) => {
    setLocalValue(_value);
  }, []);

  const renderWeekPickerDay = useCallback(
    (
      _date: DateTime,
      dateRangePickerDayProps: DateRangePickerDayProps<DateTime>
    ) => {
      return (
        <DateRangePickerDay
          classes={PickersDayClasses}
          {...dateRangePickerDayProps}
        />
      );
    },
    [PickersDayClasses]
  );

  return (
    <>
      <Button
        data-testid="insights-date-range-picker-btn"
        className={classes.button}
        onClick={handleClick}
        disableRipple
        disableFocusRipple
        disableTouchRipple
      >
        <Typography
          className={classes.period}
          data-testid="insights-date-range-string"
          variant="h6"
        >
          {label}
        </Typography>
      </Button>
      <Popover
        data-testid="insights-date-range-picker"
        classes={popoverClasses}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <StaticDateRangePicker
          displayStaticWrapperAs="desktop"
          value={localValue}
          minDate={MIN_SUPPORTED_DATE}
          onChange={handleDateChange}
          renderDay={renderWeekPickerDay}
          renderInput={(startProps, endProps) => (
            <React.Fragment>
              <TextField {...startProps} />
              <Box sx={{ mx: 2 }}> to </Box>
              <TextField {...endProps} />
            </React.Fragment>
          )}
          onAccept={handleAccept}
        />
      </Popover>
    </>
  );
};

export default React.memo(InsightsDateRangePicker);
